import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import { Carousel } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Utils, authJsonHeaders, handleSessionExpired, selectStyles, PLAN_TYPE_CODES, METRIC_TYPE_CODES, BILLING_CURRENCY_CODES, findOptionByValue, resolveBillingGeoHeaders } from '../../utils';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import Swal from "sweetalert2";
import Modal from '../common/Modal';

const TABS = [
    { key: 'plans', label: 'Plans' },
    { key: 'free_limits', label: 'Free Limits' },
    { key: 'pricing', label: 'Pricing' },
];

const planTypeSelectOptions = PLAN_TYPE_CODES.map((o) => ({ value: o.value, label: o.label }));
const fieldErrorStyle = { color: '#dc2626', fontSize: '12px', margin: '4px 0 0' };

const planCardListStyle = { listStyle: 'none', padding: 0, margin: 0 };
const planCardListItemStyle = { fontSize: '13px', color: '#6b7280', marginBottom: '6px' };

function GrowthPlanPricingCarousel({ pricingAll }) {
    const carouselRef = useRef(null);

    const metricTypes = [...new Set((pricingAll || []).map((r) => Number(r.type)).filter((t) => t !== 0))].sort((a, b) => a - b);
    if (!metricTypes.length) return null;

    const formatMetricPrice = (type, currencyValue) => {
        const row = pricingAll.find((r) => Number(r.type) === type && Number(r.currency) === currencyValue);
        if (!row) return '—';
        const symbol = findOptionByValue(BILLING_CURRENCY_CODES, currencyValue)?.symbol ?? '';
        return `${symbol}${parseFloat(row.price)}`;
    };

    const renderMetricList = (currencyValue) => (
        <ul style={planCardListStyle}>
            {metricTypes.map((type) => (
                <li key={type} style={planCardListItemStyle}>
                    {findOptionByValue(METRIC_TYPE_CODES, type)?.label}: {formatMetricPrice(type, currencyValue)}
                </li>
            ))}
        </ul>
    );

    const handleAreaClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x < rect.width / 2) carouselRef.current?.prev();
        else carouselRef.current?.next();
    };

    if (BILLING_CURRENCY_CODES.length <= 1) {
        const currency = BILLING_CURRENCY_CODES[0];
        return renderMetricList(currency?.value);
    }

    return (
        <div
            className="growth-plan-pricing-carousel-wrap"
            style={{ position: 'relative', userSelect: 'none', WebkitUserSelect: 'none', cursor: 'pointer' }}
            onClick={handleAreaClick}
        >
            <style>{`
                .growth-plan-pricing-carousel .carousel-item,
                .growth-plan-pricing-carousel .carousel-inner {
                    min-height: auto;
                }
            `}</style>
            <Carousel
                ref={carouselRef}
                interval={4000}
                indicators={false}
                controls={false}
                pause="hover"
                touch
                wrap
                className="growth-plan-pricing-carousel"
            >
                {BILLING_CURRENCY_CODES.map((c) => (
                    <Carousel.Item key={c.value}>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 8px', fontWeight: 600, textAlign: 'center' }}>
                            {c.label} ({c.symbol})
                        </p>
                        {renderMetricList(c.value)}
                    </Carousel.Item>
                ))}
            </Carousel>
        </div>
    );
}
const tabBtnStyle = (active) => ({
    padding: '12px 24px',
    border: 'none',
    background: active ? '#4f46e5' : 'transparent',
    color: active ? 'white' : '#6b7280',
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    borderRadius: active ? '8px 8px 0 0' : '8px 8px 0 0',
    fontSize: '14px',
});

const AdminPlans = () => {
    const [activeTab, setActiveTab] = useState('plans');
    const [plans, setPlans] = useState([]);
    const [catalogFreeLimits, setCatalogFreeLimits] = useState([]);
    const [catalogPricing, setCatalogPricing] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showPlanModal, setShowPlanModal] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [planForm, setPlanForm] = useState({
        name: '', plan_type: null, description: '', subtitle: '', btn_text: 'Get Started',
        is_contact_sales: 0, status: 1, sort_order: 0,
    });

    const [limitModal, setLimitModal] = useState({ show: false, row: null, type: null, limit_value: '' });
    const [limitFieldErrors, setLimitFieldErrors] = useState({});
    const [pricingModal, setPricingModal] = useState({ show: false, type: null, prices: {} });
    const [pricingFieldErrors, setPricingFieldErrors] = useState({});

    const usedPlanTypes = useMemo(
        () => new Set(plans.map((p) => Number(p.plan_type)).filter(Boolean)),
        [plans]
    );

    const availablePlanTypeOptions = useMemo(() => {
        if (editingPlan) {
            return planTypeSelectOptions.filter((o) => o.value === Number(editingPlan.plan_type));
        }
        return planTypeSelectOptions.filter((o) => !usedPlanTypes.has(o.value));
    }, [editingPlan, usedPlanTypes]);

    const freeLimitRows = useMemo(() => {
        return (catalogFreeLimits || [])
            .slice()
            .sort((a, b) => Number(a.type) - Number(b.type))
            .map((row) => ({
                ...row,
                label: findOptionByValue(METRIC_TYPE_CODES, row.type)?.label ?? `Type ${row.type}`,
            }));
    }, [catalogFreeLimits]);

    const availableLimitMetricOptions = useMemo(() => {
        const used = new Set(freeLimitRows.map((r) => Number(r.type)));
        return METRIC_TYPE_CODES
            .filter((m) => m.value !== 0 && !used.has(m.value))
            .map((m) => ({ value: m.value, label: m.label }));
    }, [freeLimitRows]);

    const pricingGroupedRows = useMemo(() => {
        const pricing = catalogPricing || [];
        const usedTypes = [...new Set(pricing.map((r) => Number(r.type)))].sort((a, b) => a - b);
        return usedTypes.map((type) => {
            const rows = pricing.filter((r) => Number(r.type) === type);
            const prices = {};
            BILLING_CURRENCY_CODES.forEach((c) => {
                const row = rows.find((r) => Number(r.currency) === c.value);
                prices[c.value] = row?.price ?? null;
            });
            return {
                type,
                label: findOptionByValue(METRIC_TYPE_CODES, type)?.label ?? `Type ${type}`,
                prices,
                rows,
            };
        });
    }, [catalogPricing]);

    const availablePricingMetricOptions = useMemo(() => {
        const used = new Set((catalogPricing || []).map((r) => Number(r.type)));
        return METRIC_TYPE_CODES
            .filter((m) => !used.has(m.value))
            .map((m) => ({ value: m.value, label: m.label }));
    }, [catalogPricing]);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const geoHeaders = await resolveBillingGeoHeaders({ optional: true });
            const response = await fetch(Utils.baseurl + 'admin/plans', {
                headers: { ...authJsonHeaders(Utils.storageKeys.adminToken), ...geoHeaders },
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setPlans(result.data || []);
                setCatalogFreeLimits(result.free_limits || []);
                setCatalogPricing(result.pricing || []);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Something went wrong');
        }
        setLoading(false);
    };

    const clearFieldError = (key) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const validatePlanForm = () => {
        const errors = {};
        if (!String(planForm.name || '').trim()) errors.name = 'Plan name is required';
        if (!planForm.plan_type) errors.plan_type = 'Plan type is required';
        if (!String(planForm.btn_text || '').trim()) errors.btn_text = 'Button text is required';
        return errors;
    };

    const closePlanModal = () => {
        setShowPlanModal(false);
        setFieldErrors({});
    };

    const handleEditPlan = (plan) => {
        setEditingPlan(plan);
        setFieldErrors({});
        setPlanForm({
            name: plan.name || '',
            plan_type: Number(plan.plan_type) || null,
            description: plan.description || '',
            subtitle: plan.subtitle || '',
            btn_text: plan.btn_text || 'Get Started',
            is_contact_sales: plan.is_contact_sales ? 1 : 0,
            status: plan.status ?? 1,
            sort_order: plan.sort_order || 0,
        });
        setShowPlanModal(true);
    };

    const handleNewPlan = () => {
        setEditingPlan(null);
        setFieldErrors({});
        const firstAvailable = planTypeSelectOptions.find((o) => !usedPlanTypes.has(o.value));
        setPlanForm({
            name: '', plan_type: firstAvailable?.value ?? null, description: '', subtitle: '', btn_text: 'Get Started',
            is_contact_sales: 0, status: 1, sort_order: plans.length,
        });
        setShowPlanModal(true);
    };

    const handleSavePlan = async () => {
        const errors = validatePlanForm();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/save', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ ...planForm, id: editingPlan?.id }),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Plan saved successfully');
                fetchPlans();
                closePlanModal();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error saving plan:', error);
            toast.error('Something went wrong');
        }
    };

    const handleDeletePlan = async (id) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you want to delete this plan?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete",
        });
        if (!confirm.isConfirmed) return;
        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/delete', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ id })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Plan deleted successfully');
                fetchPlans();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error deleting plan:', error);
            toast.error('Something went wrong');
        }
    };

    const openLimitModal = (row = null) => {
        if (row) {
            setLimitModal({ show: true, row, type: Number(row.type), limit_value: String(row.limit_value ?? '') });
        } else {
            const first = availableLimitMetricOptions[0];
            setLimitModal({ show: true, row: null, type: first?.value ?? null, limit_value: '' });
        }
        setLimitFieldErrors({});
    };

    const closeLimitModal = () => {
        setLimitModal({ show: false, row: null, type: null, limit_value: '' });
        setLimitFieldErrors({});
    };

    const handleSaveLimit = async () => {
        const errors = {};
        if (!limitModal.type) errors.type = 'Metric type is required';
        if (limitModal.limit_value === '' || limitModal.limit_value == null) errors.limit_value = 'Limit value is required';
        setLimitFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const merged = METRIC_TYPE_CODES.filter((m) => m.value !== 0).map((m) => {
            const existing = freeLimitRows.find((r) => Number(r.type) === m.value);
            if (Number(limitModal.type) === m.value) {
                return { type: m.value, limit_value: limitModal.limit_value };
            }
            if (existing) return { type: m.value, limit_value: existing.limit_value };
            return null;
        }).filter(Boolean);

        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/free-limits/save', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ free_limits: merged }),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Free limit saved');
                fetchPlans();
                closeLimitModal();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error saving free limit:', error);
            toast.error('Something went wrong');
        }
    };

    const handleDeleteLimit = async (row) => {
        if (!row?.id) return;
        const confirm = await Swal.fire({
            title: "Remove limit?",
            text: `Remove limit for ${row.label}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, remove",
        });
        if (!confirm.isConfirmed) return;
        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/free-limits/delete', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ id: row.id }),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Limit removed');
                fetchPlans();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            toast.error('Something went wrong');
        }
    };

    const buildPricesFromGroup = (group) => {
        const prices = {};
        BILLING_CURRENCY_CODES.forEach((c) => {
            prices[c.value] = group?.prices?.[c.value] != null ? String(group.prices[c.value]) : '';
        });
        return prices;
    };

    const openPricingModal = (group = null) => {
        if (group) {
            setPricingModal({
                show: true,
                type: Number(group.type),
                prices: buildPricesFromGroup(group),
            });
        } else {
            const first = availablePricingMetricOptions[0];
            const prices = {};
            BILLING_CURRENCY_CODES.forEach((c) => { prices[c.value] = ''; });
            setPricingModal({
                show: true,
                type: first?.value ?? null,
                prices,
            });
        }
        setPricingFieldErrors({});
    };

    const closePricingModal = () => {
        setPricingModal({ show: false, type: null, prices: {} });
        setPricingFieldErrors({});
    };

    const handleSavePricing = async () => {
        const errors = {};
        if (pricingModal.type == null || pricingModal.type === '') errors.type = 'Metric type is required';
        BILLING_CURRENCY_CODES.forEach((c) => {
            const val = pricingModal.prices?.[c.value];
            if (val === '' || val == null) errors[`price_${c.value}`] = `${c.label} price is required`;
        });
        setPricingFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const pricing = BILLING_CURRENCY_CODES.map((c) => ({
            type: pricingModal.type,
            currency: c.value,
            price: pricingModal.prices[c.value],
        }));

        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/pricing/save', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ pricing }),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Pricing saved');
                fetchPlans();
                closePricingModal();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error saving pricing:', error);
            toast.error('Something went wrong');
        }
    };

    const handleDeletePricing = async (group) => {
        if (!group?.type) return;
        const confirm = await Swal.fire({
            title: "Remove pricing?",
            text: `Remove all currency pricing for ${group.label}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, remove",
        });
        if (!confirm.isConfirmed) return;
        try {
            const response = await fetch(Utils.baseurl + 'admin/plans/pricing/delete', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ type: group.type }),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                toast.success(result.message || 'Pricing removed');
                fetchPlans();
            } else {
                toast.error(result.message || 'Something went wrong');
            }
        } catch (error) {
            toast.error('Something went wrong');
        }
    };

    const formatPricingCell = (price, currencyValue) => {
        if (price == null || price === '') return '—';
        const symbol = findOptionByValue(BILLING_CURRENCY_CODES, currencyValue)?.symbol ?? '';
        return `${symbol}${parseFloat(price)}`;
    };

    const renderCardPrice = (plan) => {
        if (plan.is_contact_sales) return 'Contact Sales';
        const currencySymbol = findOptionByValue(BILLING_CURRENCY_CODES, plan.price_currency)?.symbol ?? '';
        if (Number(plan.plan_type) === PLAN_TYPE_CODES[0].value) {
            return `${currencySymbol}0`;
        }
        if (Number(plan.plan_type) === PLAN_TYPE_CODES[1].value) {
            return `${currencySymbol}${plan.price ?? 0}`;
        }
        if (plan.price != null) return `${currencySymbol}${plan.price}`;
        return '—';
    };

    const renderPlanCardExtras = (plan) => {
        const planType = Number(plan.plan_type);
        const listStyle = { listStyle: 'none', padding: 0, margin: '12px 0 16px', flex: 1 };

        if (planType === PLAN_TYPE_CODES[0].value) {
            const limits = (plan.free_limits || []).slice().sort((a, b) => Number(a.type) - Number(b.type));
            if (!limits.length) {
                return plan.subtitle ? (
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px', flex: 1 }}>{plan.subtitle}</p>
                ) : null;
            }
            return (
                <ul style={listStyle}>
                    {limits.map((row) => (
                        <li key={row.id || row.type} style={planCardListItemStyle}>
                            {findOptionByValue(METRIC_TYPE_CODES, row.type)?.label}: {Number(row.limit_value).toLocaleString()}/mo
                        </li>
                    ))}
                </ul>
            );
        }

        if (planType === PLAN_TYPE_CODES[1].value) {
            const pricingAll = catalogPricing.length ? catalogPricing : (plan.pricing || []);
            const metricTypes = [...new Set(pricingAll.map((r) => Number(r.type)).filter((t) => t !== 0))].sort((a, b) => a - b);
            if (!metricTypes.length) {
                return plan.subtitle ? (
                    <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px', flex: 1 }}>{plan.subtitle}</p>
                ) : null;
            }
            return (
                <div style={{ flex: 1, margin: '12px 0 16px' }}>
                    <GrowthPlanPricingCarousel pricingAll={pricingAll} />
                </div>
            );
        }

        if (plan.subtitle) {
            return <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px', flex: 1 }}>{plan.subtitle}</p>;
        }
        return null;
    };

    const inputStyle = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' };
    const getInputStyle = (key, errors) => ({
        ...inputStyle,
        border: `1px solid ${errors[key] ? '#dc2626' : '#d1d5db'}`,
    });
    const getSelectStyles = (key, errors) => ({
        ...selectStyles,
        control: (base, state) => ({
            ...(selectStyles.control ? selectStyles.control(base, state) : base),
            borderColor: errors[key] ? '#dc2626' : (state.isFocused ? 'rgb(67, 70, 206)' : '#ced4da'),
        }),
    });

    const tableWrapStyle = { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' };
    const thStyle = { padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' };
    const tdStyle = { padding: '14px 16px', fontSize: '14px', color: '#374151', borderBottom: '1px solid #f3f4f6' };
    const actionBtnStyle = { padding: '6px 8px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' };

    const renderPlansTab = () => (
        <>
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={handleNewPlan}
                    disabled={availablePlanTypeOptions.length === 0 && !editingPlan}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px',
                        background: availablePlanTypeOptions.length === 0 && !editingPlan ? '#9ca3af' : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white', border: 'none', borderRadius: '8px', cursor: availablePlanTypeOptions.length === 0 && !editingPlan ? 'not-allowed' : 'pointer', fontWeight: '500'
                    }}
                ><MdAdd size={20} /> Add Plan</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
                {loading ?
                    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
                    : plans.length === 0 ?
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', gridColumn: '1/-1' }}>No plans configured.</div> :
                        plans.map(plan => (
                            <div
                                key={plan.id}
                                style={{
                                    background: 'white', borderRadius: '16px', padding: '24px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'relative',
                                    display: 'flex', flexDirection: 'column', minHeight: '100%',
                                }}
                            >
                                {!plan.status && <span style={{ position: 'absolute', top: '12px', right: '12px', padding: '4px 8px', background: '#fef2f2', color: '#dc2626', borderRadius: '4px', fontSize: '11px' }}>Inactive</span>}
                                <h3 style={{ margin: '0 0 8px 0', color: '#1a1a1a' }}>{plan.name}</h3>
                                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>{plan.plan_type_label || findOptionByValue(PLAN_TYPE_CODES, plan.plan_type)?.label || '—'}</div>
                                <div style={{ fontSize: '32px', fontWeight: '700', color: '#4f46e5', marginBottom: '8px' }}>{renderCardPrice(plan)}</div>
                                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>{plan.description}</p>
                                {renderPlanCardExtras(plan)}
                                <div style={{ marginTop: 'auto', paddingTop: '16px', display: 'flex', gap: '8px' }}>
                                    <button onClick={() => handleEditPlan(plan)} style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><MdEdit size={16} /> Edit</button>
                                    <button onClick={() => handleDeletePlan(plan.id)} style={{ display: 'none', padding: '10px', background: '#fef2f2', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#dc2626' }}><MdDelete size={16} /></button>
                                </div>
                            </div>
                        ))
                }
            </div>
        </>
    );

    const renderFreeLimitsTab = () => (
        <>
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={() => openLimitModal()}
                    disabled={availableLimitMetricOptions.length === 0}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px',
                        background: availableLimitMetricOptions.length === 0 ? '#9ca3af' : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white', border: 'none', borderRadius: '8px', cursor: availableLimitMetricOptions.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500'
                    }}
                ><MdAdd size={20} /> Add Limit</button>
            </div>
            <div style={tableWrapStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Metric</th>
                            <th style={thStyle}>Limit</th>
                            <th style={{ ...thStyle, width: '120px', textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {freeLimitRows.length === 0 ? (
                            <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No limits configured yet.</td></tr>
                        ) : freeLimitRows.map((row) => (
                            <tr key={row.id || row.type}>
                                <td style={tdStyle}>{row.label}</td>
                                <td style={tdStyle}>{Number(row.limit_value).toLocaleString()}</td>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <button type="button" onClick={() => openLimitModal(row)} style={{ ...actionBtnStyle, background: '#f3f4f6', marginRight: '6px' }} title="Edit"><MdEdit size={18} /></button>
                                    <button type="button" onClick={() => handleDeleteLimit(row)} style={{ ...actionBtnStyle, background: '#fef2f2', color: '#dc2626' }} title="Delete"><MdDelete size={18} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );

    const renderPricingTab = () => (
        <>
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={() => openPricingModal()}
                    disabled={availablePricingMetricOptions.length === 0}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px',
                        background: availablePricingMetricOptions.length === 0 ? '#9ca3af' : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white', border: 'none', borderRadius: '8px', cursor: availablePricingMetricOptions.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500'
                    }}
                ><MdAdd size={20} /> Add Pricing</button>
            </div>
            <div style={tableWrapStyle}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Metric</th>
                            {BILLING_CURRENCY_CODES.map((c) => (
                                <th key={c.value} style={thStyle}>{c.label}</th>
                            ))}
                            <th style={{ ...thStyle, width: '120px', textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pricingGroupedRows.length === 0 ? (
                            <tr><td colSpan={BILLING_CURRENCY_CODES.length + 2} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No pricing configured yet.</td></tr>
                        ) : pricingGroupedRows.map((group) => (
                            <tr key={group.type}>
                                <td style={tdStyle}>{group.label}</td>
                                {BILLING_CURRENCY_CODES.map((c) => (
                                    <td key={c.value} style={tdStyle}>{formatPricingCell(group.prices[c.value], c.value)}</td>
                                ))}
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <button type="button" onClick={() => openPricingModal(group)} style={{ ...actionBtnStyle, background: '#f3f4f6', marginRight: '6px' }} title="Edit"><MdEdit size={18} /></button>
                                    <button type="button" onClick={() => handleDeletePricing(group)} style={{ ...actionBtnStyle, background: '#fef2f2', color: '#dc2626' }} title="Delete"><MdDelete size={18} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );

    const limitMetricOptions = limitModal.row
        ? [{ value: limitModal.type, label: findOptionByValue(METRIC_TYPE_CODES, limitModal.type)?.label }]
        : availableLimitMetricOptions;

    const pricingMetricOptions = pricingModal.type != null && !availablePricingMetricOptions.some((o) => o.value === pricingModal.type)
        ? [{ value: pricingModal.type, label: findOptionByValue(METRIC_TYPE_CODES, pricingModal.type)?.label }]
            .concat(availablePricingMetricOptions)
        : availablePricingMetricOptions;

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Plans Management</h1>
                <p style={{ color: '#6b7280', marginTop: '8px' }}>Configure subscription plans, free limits, and pricing</p>
            </div>

            <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e5e7eb', marginBottom: '24px' }}>
                {TABS.map((tab) => (
                    <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={tabBtnStyle(activeTab === tab.key)}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'plans' && renderPlansTab()}
            {activeTab === 'free_limits' && renderFreeLimitsTab()}
            {activeTab === 'pricing' && renderPricingTab()}

            {showPlanModal && (
                <Modal
                    title={editingPlan ? 'Edit Plan' : 'New Plan'}
                    onClose={closePlanModal}
                    maxWidth="600px"
                    footer={<>
                        <button type="button" onClick={closePlanModal} style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                        <button type="button" onClick={handleSavePlan} style={{ padding: '10px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Save Plan</button>
                    </>}
                >
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>Plan Name</label>
                                <input type="text" value={planForm.name} onChange={(e) => { setPlanForm(p => ({ ...p, name: e.target.value })); clearFieldError('name'); }} style={getInputStyle('name', fieldErrors)} />
                                {fieldErrors.name && <p style={fieldErrorStyle}>{fieldErrors.name}</p>}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Plan Type</label>
                                    <Select
                                        options={availablePlanTypeOptions}
                                        value={availablePlanTypeOptions.find((o) => o.value === planForm.plan_type) || null}
                                        onChange={(opt) => {
                                            clearFieldError('plan_type');
                                            const val = opt?.value ?? null;
                                            setPlanForm((p) => ({
                                                ...p,
                                                plan_type: val,
                                                is_contact_sales: val === PLAN_TYPE_CODES[2].value ? 1 : p.is_contact_sales,
                                            }));
                                        }}
                                        isDisabled={Boolean(editingPlan)}
                                        isClearable={false}
                                        styles={getSelectStyles('plan_type', fieldErrors)}
                                        menuPortalTarget={document.body}
                                        placeholder="Select plan type"
                                    />
                                    {fieldErrors.plan_type && <p style={fieldErrorStyle}>{fieldErrors.plan_type}</p>}
                                </div>
                                <div>
                                    <label style={labelStyle}>Sort Order</label>
                                    <input type="number" value={planForm.sort_order} onChange={(e) => setPlanForm(p => ({ ...p, sort_order: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} />
                                </div>
                            </div>
                            <div>
                                <label style={labelStyle}>Description</label>
                                <textarea value={planForm.description} onChange={(e) => setPlanForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
                            </div>
                            <div>
                                <label style={labelStyle}>Subtitle</label>
                                <input type="text" value={planForm.subtitle} onChange={(e) => setPlanForm(p => ({ ...p, subtitle: e.target.value }))} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Button Text</label>
                                <input type="text" value={planForm.btn_text} onChange={(e) => { setPlanForm(p => ({ ...p, btn_text: e.target.value })); clearFieldError('btn_text'); }} style={getInputStyle('btn_text', fieldErrors)} />
                                {fieldErrors.btn_text && <p style={fieldErrorStyle}>{fieldErrors.btn_text}</p>}
                            </div>
                            {Number(planForm.plan_type) !== PLAN_TYPE_CODES[2].value && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><input type="checkbox" id="is_contact_sales" checked={planForm.is_contact_sales === 1} onChange={(e) => setPlanForm(p => ({ ...p, is_contact_sales: e.target.checked ? 1 : 0 }))} /><label htmlFor="is_contact_sales">Contact Sales plan</label></div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><input type="checkbox" id="status" checked={planForm.status === 1} onChange={(e) => setPlanForm(p => ({ ...p, status: e.target.checked ? 1 : 0 }))} /><label htmlFor="status">Active</label></div>
                        </div>
                </Modal>
            )}

            {limitModal.show && (
                <Modal
                    title={limitModal.row ? 'Edit Free Limit' : 'Add Free Limit'}
                    onClose={closeLimitModal}
                    maxWidth="480px"
                    footer={<>
                        <button type="button" onClick={closeLimitModal} style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                        <button type="button" onClick={handleSaveLimit} style={{ padding: '10px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Save</button>
                    </>}
                >
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>Metric</label>
                                <Select
                                    options={limitMetricOptions}
                                    value={limitMetricOptions.find((o) => o.value === limitModal.type) || null}
                                    onChange={(opt) => { setLimitModal((m) => ({ ...m, type: opt?.value ?? null })); setLimitFieldErrors({}); }}
                                    isDisabled={Boolean(limitModal.row)}
                                    styles={getSelectStyles('type', limitFieldErrors)}
                                    menuPortalTarget={document.body}
                                />
                                {limitFieldErrors.type && <p style={fieldErrorStyle}>{limitFieldErrors.type}</p>}
                            </div>
                            <div>
                                <label style={labelStyle}>Limit Value</label>
                                <input type="number" min="0" value={limitModal.limit_value} onChange={(e) => { setLimitModal((m) => ({ ...m, limit_value: e.target.value })); setLimitFieldErrors({}); }} style={getInputStyle('limit_value', limitFieldErrors)} />
                                {limitFieldErrors.limit_value && <p style={fieldErrorStyle}>{limitFieldErrors.limit_value}</p>}
                            </div>
                        </div>
                </Modal>
            )}

            {pricingModal.show && (
                <Modal
                    title={pricingGroupedRows.some((g) => g.type === pricingModal.type) ? 'Edit Pricing' : 'Add Pricing'}
                    onClose={closePricingModal}
                    maxWidth="560px"
                    footer={<>
                        <button type="button" onClick={closePricingModal} style={{ padding: '10px 18px', background: '#f3f4f6', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                        <button type="button" onClick={handleSavePricing} style={{ padding: '10px 18px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Save</button>
                    </>}
                >
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>Metric</label>
                                <Select
                                    options={pricingMetricOptions}
                                    value={pricingMetricOptions.find((o) => o.value === pricingModal.type) || null}
                                    onChange={(opt) => {
                                        setPricingModal((m) => ({ ...m, type: opt?.value ?? null }));
                                        setPricingFieldErrors({});
                                    }}
                                    isDisabled={pricingGroupedRows.some((g) => g.type === pricingModal.type)}
                                    styles={getSelectStyles('type', pricingFieldErrors)}
                                    menuPortalTarget={document.body}
                                    placeholder="Select metric"
                                />
                                {pricingFieldErrors.type && <p style={fieldErrorStyle}>{pricingFieldErrors.type}</p>}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                {BILLING_CURRENCY_CODES.map((c) => (
                                    <div key={c.value}>
                                        <label style={labelStyle}>{c.label} ({c.symbol})</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={pricingModal.prices?.[c.value] ?? ''}
                                            onChange={(e) => {
                                                setPricingModal((m) => ({
                                                    ...m,
                                                    prices: { ...m.prices, [c.value]: e.target.value },
                                                }));
                                                setPricingFieldErrors((prev) => {
                                                    const next = { ...prev };
                                                    delete next[`price_${c.value}`];
                                                    return next;
                                                });
                                            }}
                                            style={getInputStyle(`price_${c.value}`, pricingFieldErrors)}
                                        />
                                        {pricingFieldErrors[`price_${c.value}`] && (
                                            <p style={fieldErrorStyle}>{pricingFieldErrors[`price_${c.value}`]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                </Modal>
            )}
        </div>
    );
};

export default AdminPlans;
